// ═══════════════════════════════════════════════════════════════
// KOTCH - GAME CONSTANTS
// ═══════════════════════════════════════════════════════════════

import type { AIConfig, AIPersonality, GameConfig } from '../../types/kotch';

// ─────────────────────────────────────────────────────────────────
// AI PERSONALITY CONFIGURATIONS
// ─────────────────────────────────────────────────────────────────

export const AI_PERSONALITIES: Record<AIPersonality, AIConfig> = {
  easy: {
    smartPlayChance: 0.3,        // 30% chance of strategic play
    usesCardCounting: false,
    usesLaneStrategy: false,
    bluffChance: 0.1,            // Rarely uses special cards strategically
    conserveCardsChance: 0.1,    // Rarely holds back
  },
  medium: {
    smartPlayChance: 0.65,       // 65% chance of strategic play
    usesCardCounting: false,
    usesLaneStrategy: true,
    bluffChance: 0.25,           // Sometimes bluffs with specials
    conserveCardsChance: 0.3,    // Sometimes conserves cards
  },
  hard: {
    smartPlayChance: 0.9,        // 90% chance of strategic play
    usesCardCounting: true,      // Tracks played cards
    usesLaneStrategy: true,
    bluffChance: 0.4,            // Strategic use of special cards
    conserveCardsChance: 0.5,    // Resource management
  },
};

// ─────────────────────────────────────────────────────────────────
// DEFAULT GAME SETTINGS
// ─────────────────────────────────────────────────────────────────

export const DEFAULT_GAME_CONFIG: GameConfig = {
  player1Setup: { name: 'Player 1', isAI: false },
  player2Setup: { name: 'Opponent', isAI: true, aiPersonality: 'medium' },
  cardsPerBattle: 10,
  battlesToWin: 2,
};

// ─────────────────────────────────────────────────────────────────
// GAME LIMITS
// ─────────────────────────────────────────────────────────────────

export const CARDS_PER_BATTLE = 10;
export const TOTAL_DECK_SIZE = 47;
export const MAX_BATTLES = 3;
export const BATTLES_TO_WIN = 2;
export const LANES_TO_WIN_ROUND = 2;

// ─────────────────────────────────────────────────────────────────
// DEFAULT PLAYER NAMES
// ─────────────────────────────────────────────────────────────────

export const DEFAULT_PLAYER_NAMES = {
  player1: 'Commander',
  player2: 'Opponent',
};

export const AI_PLAYER_NAMES: string[] = [
  'General Koth',
  'Warlord Vex',
  'Marshal Dorn',
  'Captain Sera',
  'Lord Ashford',
  'Lady Morwen',
];

// ─────────────────────────────────────────────────────────────────
// TIMING CONSTANTS (milliseconds)
// ─────────────────────────────────────────────────────────────────

export const TIMING = {
  CARD_FLIP_ANIMATION_MS: 600,
  LANE_RESOLUTION_DELAY_MS: 800,
  AI_THINKING_DELAY_MS: 1200,
  AI_COMMIT_DELAY_MS: 400,
  MESSAGE_DISPLAY_MS: 2000,
  TRANSITION_DELAY_MS: 500,
  BATTLE_START_DELAY_MS: 1500,
};

// ─────────────────────────────────────────────────────────────────
// LANE NAMES
// ─────────────────────────────────────────────────────────────────

export const LANE_NAMES = {
  left: 'Left Flank',
  center: 'Center',
  right: 'Right Flank',
};

export const LANE_SHORT_NAMES = {
  left: 'L',
  center: 'C',
  right: 'R',
};

// ─────────────────────────────────────────────────────────────────
// FLAVOR TEXT
// ─────────────────────────────────────────────────────────────────

export const BATTLE_START_MESSAGES = [
  'The armies gather...',
  'War drums echo across the field.',
  'Steel meets steel.',
  'The battle begins.',
  'To victory!',
];

export const ROUND_START_MESSAGES = [
  'Deploy your forces.',
  'Choose your ground.',
  'The flanks await.',
  'Position your troops.',
];

export const ROUND_WIN_MESSAGES = [
  'A decisive victory!',
  'The field is yours.',
  'Your tactics prevail.',
  'Well fought.',
];

export const ROUND_LOSE_MESSAGES = [
  'A setback, but not defeat.',
  'Regroup and strike again.',
  'The enemy advances.',
  'Hold the line.',
];

export const BATTLE_WIN_MESSAGES = [
  'Victory is yours!',
  'The enemy retreats!',
  'A glorious triumph!',
  'Your banner flies high.',
];

export const MATCH_WIN_MESSAGES = [
  'Total victory! You are the supreme commander!',
  'The war is won! Your legend grows.',
  'Absolute dominance! History will remember this day.',
];

export const MATCH_LOSE_MESSAGES = [
  'Defeat... but you fought with honor.',
  'The campaign is lost. Another day, another war.',
  'Your forces are scattered. Rebuild and return.',
];

export function getRandomMessage(messages: string[]): string {
  return messages[Math.floor(Math.random() * messages.length)];
}

// ─────────────────────────────────────────────────────────────────
// COMBAT RESULT DESCRIPTIONS
// ─────────────────────────────────────────────────────────────────

export const COMBAT_DESCRIPTIONS = {
  spearman_beats_rider: 'Spears break the cavalry charge!',
  rider_beats_archer: 'Cavalry overruns the archers!',
  archer_beats_spearman: 'Arrows rain down on the infantry!',
  knight_beats_basic: 'The knight scatters the troops!',
  spy_beats_knight: 'An assassin strikes from the shadows!',
  basic_beats_spy: 'The spy is exposed and captured!',
  higher_power: 'Superior strength wins the clash!',
  draw: 'Neither side gains ground.',
  unopposed: 'The lane is taken without contest.',
};
